/**
 * Bottom-left panel (02 §5): the 3-segment wound bar with the bleed-out timer, the fatigue bar
 * that fills as the ranger tires, and the stance tags: STAND, SNEAK (the crouched walk, ROUND7) or CRAWL.
 */

import './statusPanel.css';
import { TICK_RATE } from '../../sim/types';
import type { World } from '../../sim/types';
import { clamp, el, mmss, show, Shown } from './dom';

const FATIGUE_W = 60;
/** Not in the contract: 45 s bleed-out (02 §5), used only if `bleedTicks` turns out to count up. */
const BLEED_OUT_TICKS = 45 * TICK_RATE;

export class StatusPanel {
  readonly el: HTMLElement;
  private readonly wounds: HTMLElement;
  private readonly segs: HTMLElement[];
  private readonly down: HTMLElement;
  private readonly fill: HTMLElement;
  private readonly winded: HTMLElement;
  private readonly stance: HTMLElement;
  private readonly run: HTMLElement;
  private readonly cover: HTMLElement;
  private readonly shown = new Shown();
  private lastWounds = 0;
  private hitFlips = 0;
  private wasDowned = false;
  private bleedCountsUp = false;

  constructor() {
    this.el = el('div', 'hud-panel hud-bl');
    this.wounds = el('div', 'hud-wounds');
    this.segs = [el('span', 'hud-seg'), el('span', 'hud-seg'), el('span', 'hud-seg')];
    this.wounds.append(...this.segs);
    this.down = el('span', 'hud-down hud-hidden', 'DOWN');
    const woundRow = el('div', 'hud-row');
    woundRow.append(el('span', 'hud-label', 'WND'), this.wounds, this.down);

    const track = el('div', 'hud-fatigue-track');
    track.style.width = `${FATIGUE_W + 2}px`;
    this.fill = el('div', 'hud-fatigue-fill');
    track.append(this.fill);
    for (const threshold of [0.5, 0.8]) {
      const mark = el('span', 'hud-fatigue-mark');
      mark.style.left = `${Math.round(threshold * FATIGUE_W)}px`;
      track.append(mark);
    }
    this.winded = el('span', 'hud-tag hud-tag-winded hud-hidden', 'WINDED');
    const fatigueRow = el('div', 'hud-row hud-fatigue-row');
    fatigueRow.append(el('span', 'hud-label', 'FTG'), track, this.winded);

    this.stance = el('span', 'hud-tag', 'STAND');
    this.run = el('span', 'hud-tag hud-tag-run hud-hidden', 'RUN');
    this.cover = el('span', 'hud-tag hud-tag-cover hud-hidden', 'IN COVER');
    const tags = el('div', 'hud-row hud-tags');
    tags.append(this.stance, this.run, this.cover);
    this.el.append(woundRow, fatigueRow, tags);
  }

  update(world: World): void {
    const shown = this.shown;
    const ranger = world.ranger;
    const downed = ranger.status === 'downed';
    const wounds = downed ? 3 : clamp(Math.floor(ranger.wounds), 0, 3);
    if (shown.changed('wounds', wounds)) {
      this.segs.forEach((seg, i) => seg.classList.toggle('hud-seg-on', i < wounds));
      if (wounds > this.lastWounds) {
        // Alternating two identical animations restarts the flash without forcing a reflow.
        this.hitFlips++;
        this.wounds.classList.toggle('hud-hit-a', this.hitFlips % 2 === 0);
        this.wounds.classList.toggle('hud-hit-b', this.hitFlips % 2 === 1);
      }
      this.lastWounds = wounds;
    }

    if (shown.changed('downed', downed)) show(this.down, downed);
    if (downed) {
      // The contract does not say which way `bleedTicks` runs; a bleed-out that starts near
      // zero must be counting up toward the 45 s limit.
      if (!this.wasDowned) this.bleedCountsUp = ranger.bleedTicks < BLEED_OUT_TICKS / 2;
      const left = this.bleedCountsUp ? BLEED_OUT_TICKS - ranger.bleedTicks : ranger.bleedTicks;
      const seconds = Math.max(0, Math.ceil(left / TICK_RATE));
      if (shown.changed('bleed', seconds)) this.down.textContent = `DOWN ${mmss(seconds)}`;
    }
    this.wasDowned = downed;

    const fatigue = clamp(ranger.fatigue, 0, 1);
    const fillPx = Math.round(fatigue * FATIGUE_W);
    if (shown.changed('fatigue', fillPx)) this.fill.style.width = `${fillPx}px`;
    const tier = fatigue >= 0.8 ? 2 : fatigue >= 0.5 ? 1 : 0;
    if (shown.changed('fatigueTier', tier)) {
      this.fill.classList.toggle('hud-fatigue-1', tier === 1);
      this.fill.classList.toggle('hud-fatigue-2', tier === 2);
    }
    if (shown.changed('winded', ranger.winded)) show(this.winded, ranger.winded);

    // Crawl wins over sneak in the sim, so it does here: the tag says what his body is doing.
    const stance = ranger.stance === 'crawl' ? 'CRAWL' : ranger.sneaking === true ? 'SNEAK' : 'STAND';
    if (shown.changed('stance', stance)) this.stance.textContent = stance;
    if (shown.changed('run', ranger.running)) show(this.run, ranger.running);
    const inCover = ranger.inTrench && ranger.stance === 'crawl';
    if (shown.changed('cover', inCover)) show(this.cover, inCover);
  }
}
